import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import LandingPage from '../pages/LandingPage';
import { Box, CircularProgress } from '@mui/material';

/**
 * RootRedirect component handles the root path (/) routing logic:
 * - For root domains (coresupportai.com, coresupport.ai): Show landing page
 * - For app subdomains (app.coresupportai.com, app.coresupport.ai):
 *   - If authenticated: redirect to /chat
 *   - If not authenticated: redirect to /login
 * - While checking auth status: show loading spinner
 */
const RootRedirect: React.FC = () => {
  const { user, isLoading } = useAuth();
  
  // Check if we're on a root domain (should show landing page) or app subdomain (chat app)
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  const isRootDomain = hostname === 'coresupportai.com' || 
                      hostname === 'www.coresupportai.com' ||
                      hostname === 'coresupport.ai' || 
                      hostname === 'www.coresupport.ai';
  const isAppSubdomain = hostname === 'app.coresupportai.com' || 
                        hostname === 'app.coresupport.ai';
  const isDemoSubdomain = hostname === 'demo.coresupportai.com' ||
                         hostname === 'demo.coresupport.ai';

  // For demo subdomain, redirect to /demo
  if (isDemoSubdomain) {
    return <Navigate to="/demo" replace />;
  }

  // For root domains, always show landing page
  if (isRootDomain) {
    return <LandingPage />;
  }

  // For app subdomains, use authentication-based routing
  if (!isAppSubdomain) {
    // If it's neither root nor app subdomain, default to landing page
    return <LandingPage />;
  }

  // Show loading while checking authentication (for app subdomains)
  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  // If authenticated, redirect to chat
  if (user) {
    return <Navigate to="/chat" replace />;
  }

  // If not authenticated, redirect to login
  return <Navigate to="/login" replace />;
};

export default RootRedirect;

